import {
  GetOutputSchemasParams,
  QueryAndOutputs,
  TableSchema,
  TableSchemasList,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentInput,
  WorkflowComponentMetadata,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType,
  WorkflowEnvVars
} from '../../types'
import { Workflow } from '../../workflow'
import { DataWarehouseConnection } from '../../workflow/connections'
import { WorkflowComponent } from './workflowcomponent'

export const FILTER_TABLE_TRUE_NAME = 'match'
export const FILTER_TABLE_FALSE_NAME = 'unmatch'

// A component representing a filter, with two output tables (one with rows passing the filter,
// the other one with those that do not pass it)
export abstract class FilterWorkflowComponent extends WorkflowComponent {
  constructor(metadata: WorkflowComponentMetadata, inputs: WorkflowComponentInput[]) {
    const outputs = [
      {
        name: FILTER_TABLE_TRUE_NAME,
        title: 'Matched',
        description: 'Table with rows that pass the filter',
        type: WorkflowComponentOutputType.Table
      },
      {
        name: FILTER_TABLE_FALSE_NAME,
        title: 'Unmatched',
        description: 'Table with rows that do not pass the filter',
        type: WorkflowComponentOutputType.Table
      }
    ]
    super(metadata, inputs, outputs)
    this.canBeCompacted = true
  }

  abstract _getMatchCode(
    params: Record<string, unknown>,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ): Promise<string | null>

  abstract _getUnmatchCode(
    params: Record<string, unknown>,
    matchTablename: string,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ): Promise<string | null>

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const tablenameMatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_TRUE_NAME,
      codeType
    )
    const tablenameUnmatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_FALSE_NAME,
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    const match = await this._getMatchCode(
      preparedParams,
      envVars,
      workflow,
      componentName,
      connection,
      codeType,
      schemas?.output['match'] || undefined
    )
    const unmatch = await this._getUnmatchCode(
      preparedParams,
      tablenameMatch,
      envVars,
      workflow,
      componentName,
      connection,
      codeType,
      schemas?.output['unmatch'] || undefined
    )
    if (match === null || unmatch === null) {
      return null
    }
    const queryMatch = connection.getCreateTempTableQuery(tablenameMatch, match, codeType, null)

    const unmatchIsUsed =
      workflow && componentName && codeType ? workflow.isTableUsed(componentName, FILTER_TABLE_FALSE_NAME) : true
    const queryUnmatch = unmatchIsUsed
      ? connection.getCreateTempTableQuery(tablenameUnmatch, unmatch, codeType, null)
      : ''
    const query = `${queryMatch}\n${queryUnmatch}`
    const sqlret: QueryAndOutputs = { query: query, outputs: {} }
    sqlret.outputs[FILTER_TABLE_TRUE_NAME] = tablenameMatch
    sqlret.outputs[FILTER_TABLE_FALSE_NAME] = tablenameUnmatch

    return sqlret
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablenameMatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_TRUE_NAME,
      codeType
    )
    const tablenameUnmatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_FALSE_NAME,
      codeType
    )
    const outputs = {}
    outputs[FILTER_TABLE_TRUE_NAME] = tablenameMatch
    outputs[FILTER_TABLE_FALSE_NAME] = tablenameUnmatch
    return outputs
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    workflow,
    params,
    schemas,
    connection,
    componentName
  }: GetOutputSchemasParams): TableSchemasList {
    const tableInputName = this.inputs.find((input) => input.type === WorkflowComponentParamType.InputTable)
      ?.name as string
    const tablename = params[tableInputName] as string
    const columns = this._getSchemaForTablename(tablename, schemas, connection)
    const tablenameMatch = this._getDryRunOutputTableFqn(
      workflow,
      params,
      connection,
      componentName,
      FILTER_TABLE_TRUE_NAME
    )
    const tablenameUnmatch = this._getDryRunOutputTableFqn(
      workflow,
      params,
      connection,
      componentName,
      FILTER_TABLE_FALSE_NAME
    )
    return [
      {
        fqn: tablenameMatch,
        schema: columns
      },
      {
        fqn: tablenameUnmatch,
        schema: columns
      }
    ]
  }

  isOutputTableCreated(
    workflow: Workflow,
    componentName: string,
    outputName: string,
    params: Record<string, any>
  ): boolean {
    if (outputName === FILTER_TABLE_TRUE_NAME) {
      return true
    }
    if (outputName === FILTER_TABLE_FALSE_NAME) {
      const unmatchIsUsed =
        workflow && componentName ? workflow.isTableUsed(componentName, FILTER_TABLE_FALSE_NAME) : true
      return unmatchIsUsed
    }
    return false
  }

  canBeConvertedToCTE(workflow: Workflow, componentName: string): boolean {
    // return true only if one and only one of the outputs is used by other components
    const outputs = this.outputs
    const usedOutputs = outputs.filter((output) => workflow.isTableUsed(componentName, output.name))
    const canBeConverted = usedOutputs.length === 1
    return canBeConverted && super.canBeConvertedToCTE(workflow, componentName)
  }

  async getCTECode(workflowComponentCodeParams: WorkflowComponentCodeParams): Promise<string | null> {
    //return select code for match if match is used by other components, otherwise return select code for unmatch
    const { workflow, componentName, params, envVars, connection, codeType, schemas } = workflowComponentCodeParams
    const isMatchUsed = workflow.isTableUsed(componentName, FILTER_TABLE_TRUE_NAME)
    const isUnmatchUsed = workflow.isTableUsed(componentName, FILTER_TABLE_FALSE_NAME)

    if (isMatchUsed) {
      return await this._getMatchCode(
        params,
        envVars,
        workflow,
        componentName,
        connection,
        codeType,
        schemas?.output[FILTER_TABLE_TRUE_NAME] || undefined
      )
    } else if (isUnmatchUsed) {
      const tablenameMatch = connection.getOutputTableName(
        workflow.id,
        componentName,
        params,
        FILTER_TABLE_TRUE_NAME,
        codeType
      )
      return await this._getUnmatchCode(
        params,
        tablenameMatch,
        envVars,
        workflow,
        componentName,
        connection,
        codeType,
        schemas?.output[FILTER_TABLE_FALSE_NAME] || undefined
      )
    }
    return null
  }
}
